import { Upload } from 'antd'
import { downloadPdfDocument } from 'api/pdfFillerApi'
import Spinner from 'components/Spinner'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import reduce from 'lodash/reduce'
import toPairs from 'lodash/toPairs'
import trim from 'lodash/trim'
import { useLoadBusinessAssociateAgreement } from 'queries/businessAssociateAgreement/useLoadBusinessAssociateAgreement'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import TextField from 'ui/molecules/TextField'
import { sanitizeImage } from 'utils/attackPrevention/images'

import { invalidateLoadUserDetails } from '@/queries/users/useLoadUsers'
import { formatterUploadList } from '@/utils/formatterUploadList'
import cn from '@/utils/tailwind/cn'

import api from '../../api'
import Ui from '../../ui'
import Icons from '../../ui/Icons'
import { getDjangoFormsErrors } from '../../utils/formHelpers'
import { ZentakeContext } from '../../utils/ZentakeContext'

const getSrcFromFile = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(file.originFileObj)
    reader.onload = () => resolve(reader.result)
  })
}

function OrganizationTab() {
  const {
    data: businessAssociateAgreement,
    isLoading: isLoadingBusinessAssociateAgreement,
  } = useLoadBusinessAssociateAgreement()
  const history = useHistory()

  const { userDetails } = React.useContext(ZentakeContext)

  const [team, setTeam] = React.useState(() => userDetails?.team || {})
  const [errors, setErrors] = React.useState({})
  const teamDetailsBase = React.useRef({
    name: team?.name || '',
    contact_phone: team?.contact_phone || '',
    address_street_1: team?.address_street_1 || '',
    address_street_2: team?.address_street_2 || '',
    address_city: team?.address_city || '',
    address_state: team?.address_state || '',
    address_zip: team?.address_zip || '',
    image: team?.image ?? '',
  }).current
  const [fileList, setFileList] = React.useState(
    formatterUploadList(teamDetailsBase.image)
  )
  const [newTeamDetails, setNewTeamDetails] = React.useState(teamDetailsBase)
  const [hasNewImage, setHasNewImage] = React.useState(null)
  const [isUpdatingTeam, setUpdatingTeam] = React.useState(false)
  const [isEditMode, setEditMode] = React.useState(false)
  const [isBAADownloading, setIsBAADownloading] = React.useState(false)
  React.useEffect(() => {
    setTeam(userDetails?.team || {})
  }, [userDetails])

  const onUploadImage = async (file) => {
    if (Object.keys(file).length) {
      const imgFile = await sanitizeImage(file)
      return imgFile
    }
  }

  const updateImage = React.useCallback(
    async (imgFile) => {
      if (imgFile && typeof hasNewImage !== 'boolean' && hasNewImage) {
        const sanitizedFile = await onUploadImage(hasNewImage)
        return await api.updateTeamImage(team.id, sanitizedFile)
      }
      return teamDetailsBase.image
    },
    [hasNewImage, team, teamDetailsBase]
  )

  const onUpdateTeamData = React.useCallback(async () => {
    setUpdatingTeam(true)
    setErrors({})
    let willResetImage = false
    try {
      // IMAGE
      const hasEditImage = !isNil(hasNewImage)
      const teamImage = await updateImage(hasEditImage)
      if (hasEditImage) willResetImage = true

      const detailsToUpdate = reduce(
        toPairs(newTeamDetails),
        (obj, [key, value]) => {
          const trimmedValue = trim(value)
          const trimmedTeamValue = trim(team[key])

          if (key === 'image' && hasEditImage) {
            const imageHasRemoved = !isNil(hasNewImage) && !hasNewImage
            const newImageValue = imageHasRemoved ? null : teamImage

            return { ...obj, [key]: newImageValue }
          }

          if (trimmedTeamValue !== trimmedValue) {
            return { ...obj, [key]: trimmedValue }
          }

          return obj
        },
        {}
      )

      if (!newTeamDetails.contact_phone || !newTeamDetails.name) {
        if (!newTeamDetails.contact_phone) {
          setErrors((previous) => ({
            ...previous,
            contact_phone: 'This field is required',
          }))
        }
        if (!newTeamDetails.name) {
          setErrors((previous) => ({
            ...previous,
            name: 'This field is required',
          }))
        }
        setUpdatingTeam(false)
        return
      }

      if (isEmpty(detailsToUpdate)) {
        if (willResetImage) {
          setNewTeamDetails({
            ...newTeamDetails,
            image: teamImage,
          })
        }
      }

      const imageHasRemoved = !isNil(hasNewImage) && !hasNewImage
      if (!imageHasRemoved) {
        delete detailsToUpdate?.image
      }

      await api.updateTeamDetails(team.id, detailsToUpdate)
      invalidateLoadUserDetails()

      setNewTeamDetails({
        ...newTeamDetails,
        ...detailsToUpdate,
        image: teamImage,
      })
      setEditMode(false)
      toast.success('Team details updated')
    } catch (error) {
      let genericError =
        'Error updating team details. If the problem persists, please contact support.'
      if (error.status === 400) {
        const _errors = getDjangoFormsErrors(error)
        if (_errors) {
          genericError = 'Please fix the errors below'
          setErrors(_errors)
        }
      }
      toast.error(genericError)
    } finally {
      setUpdatingTeam(false)
      if (willResetImage) {
        setHasNewImage(false)
      }
    }
  }, [hasNewImage, newTeamDetails, team, updateImage])

  const updateName = React.useCallback(
    (name) => setNewTeamDetails((p) => ({ ...p, name })),
    []
  )
  const updatePhone = React.useCallback(
    (contact_phone) => setNewTeamDetails((p) => ({ ...p, contact_phone })),
    []
  )
  const updateAddress1 = React.useCallback(
    (address_street_1) =>
      setNewTeamDetails((p) => ({ ...p, address_street_1 })),
    []
  )
  const updateAddress2 = React.useCallback(
    (address_street_2) =>
      setNewTeamDetails((p) => ({ ...p, address_street_2 })),
    []
  )
  const updateCity = React.useCallback(
    (address_city) => setNewTeamDetails((p) => ({ ...p, address_city })),
    []
  )
  const updateState = React.useCallback(
    (address_state) => setNewTeamDetails((p) => ({ ...p, address_state })),
    []
  )
  const updateZip = React.useCallback(
    (address_zip) => setNewTeamDetails((p) => ({ ...p, address_zip })),
    []
  )

  const onPreview = async (file) => {
    const src = file.url || (await getSrcFromFile(file))
    const imgWindow = window.open(src)

    if (imgWindow) {
      const image = new Image()
      image.src = src
      imgWindow.document.write(image.outerHTML)
    } else {
      window.location.href = src
    }
  }

  const cancel = React.useCallback(() => {
    setErrors({})
    setNewTeamDetails(userDetails.team)
    setEditMode(false)
    setHasNewImage(null)
  }, [userDetails])

  const beforeUpload = (file) => {
    const isJpgOrPng = ['image/jpeg', 'image/png'].includes(file.type)
    if (!isJpgOrPng) {
      toast.error('You can only upload JPG or PNG files')
      return Upload.LIST_IGNORE
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      setFileList(formatterUploadList(reader.result))
      setHasNewImage(file)
    }
    return false
  }

  const onChangeUpload = (file) => {
    if (!file.fileList.length) {
      setFileList([])
      setHasNewImage(false)
      return
    }
    setFileList(file.fileList)
  }

  return (
    <>
      <div className="rounded-b rounded-tr bg-white px-4 pb-4 pt-3">
        <div className="grid grid-cols-6 gap-x-6 gap-y-4 pt-1">
          <div className="col-span-6 text-right">
            <div className="mb-0 flex flex-1 flex-shrink-0 justify-end">
              {isEditMode ? (
                <div>
                  <Ui.ButtonClear
                    className="cancel-btn text-red-500"
                    onClick={cancel}
                  >
                    Cancel
                  </Ui.ButtonClear>
                  <Ui.PrimaryButton
                    loading={isUpdatingTeam}
                    onClick={onUpdateTeamData}
                  >
                    Save
                  </Ui.PrimaryButton>
                </div>
              ) : (
                <Ui.ButtonClear
                  className="text-blue-700"
                  onClick={() => setEditMode(true)}
                >
                  <Icons.Pencil className="h-6" color="mr-2.5 text-blue-700" />
                  Edit
                </Ui.ButtonClear>
              )}
            </div>
          </div>
          <div className="col-span-6">
            <TextField
              disabled={!isEditMode}
              errorLabel={errors?.name}
              hasError={!!errors?.name}
              label="Organization name"
              name="name"
              onChange={updateName}
              placeholder="Organization name"
              type="text"
              value={newTeamDetails?.name || ''}
            />
          </div>
          <div className="col-span-6">
            <Ui.SingleLinePhone
              disabled={!isEditMode}
              errorLabel={errors?.contact_phone}
              hasError={!!errors?.contact_phone}
              label="Organization phone"
              name="contact_phone"
              onChange={updatePhone}
              placeholder="Phone number"
              value={newTeamDetails?.contact_phone || ''}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <TextField
              disabled={!isEditMode}
              errorLabel={errors?.address_street_1}
              hasError={!!errors?.address_street_1}
              label="Address 1"
              name="address_street_1"
              onChange={updateAddress1}
              placeholder="Address 1"
              type="text"
              value={newTeamDetails?.address_street_1 || ''}
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <TextField
              disabled={!isEditMode}
              errorLabel={errors?.address_street_2}
              hasError={!!errors?.address_street_2}
              label="Address 2"
              name="address_street_2"
              onChange={updateAddress2}
              placeholder="Address 2"
              type="text"
              value={newTeamDetails?.address_street_2 || ''}
            />
          </div>
          <div className="col-span-6 md:col-span-2">
            <TextField
              disabled={!isEditMode}
              errorLabel={errors?.address_city}
              hasError={!!errors?.address_city}
              label="City"
              name="address_city"
              onChange={updateCity}
              placeholder="City"
              type="text"
              value={newTeamDetails?.address_city || ''}
            />
          </div>
          <div className="col-span-6 md:col-span-2">
            <TextField
              disabled={!isEditMode}
              errorLabel={errors?.address_state}
              hasError={!!errors?.address_state}
              label="State"
              name="address_state"
              onChange={updateState}
              placeholder="State"
              type="text"
              value={newTeamDetails?.address_state || ''}
            />
          </div>
          <div className="col-span-6 md:col-span-2">
            <TextField
              disabled={!isEditMode}
              errorLabel={errors?.address_zip}
              hasError={!!errors?.address_zip}
              label="Zip/Postal Code"
              name="address_zip"
              onChange={updateZip}
              placeholder="Zip/Postal Code"
              type="text"
              value={newTeamDetails?.address_zip || ''}
            />
          </div>
          <div className="col-span-6">
            <Ui.QuestionTitle>Organization Logo</Ui.QuestionTitle>
            <Upload
              accept="image/*"
              beforeUpload={beforeUpload}
              className={cn(
                '[&_.ant-upload-list-item-container]:!h-52 [&_.ant-upload-list-item-container]:!w-52',
                '[&_.ant-upload-list-item]:!p-1 [&_.ant-upload-list-item]:before:!h-[calc(100%-8px)]',
                '[&_.ant-upload-list-item]:before:!w-[calc(100%-8px)] [&_.ant-upload-list-item]:before:rounded',
                '[&_.ant-upload]:!h-52 [&_.ant-upload]:!w-52 [&_.ant-upload]:text-base [&_.ant-upload]:text-gray-600',
                '[&_a]:rounded',
                !isEditMode && '[&_.ant-upload-select]:hidden'
              )}
              disabled={!isEditMode}
              fileList={fileList}
              listType="picture-card"
              maxCount={1}
              multiple={false}
              onChange={onChangeUpload}
              onPreview={onPreview}
            >
              {fileList.length ? 'Click to edit your logo' : 'Upload your logo'}
            </Upload>
          </div>
        </div>
        <div />
        {isLoadingBusinessAssociateAgreement ? (
          <div className="mt-4">
            <Spinner color="onLight" />
          </div>
        ) : businessAssociateAgreement?.signed ? (
          <Flex className="mt-4">
            <Button
              disabled={isBAADownloading || isLoadingBusinessAssociateAgreement}
              loading={isBAADownloading}
              onClick={async () => {
                setIsBAADownloading(true)
                try {
                  await downloadPdfDocument(
                    businessAssociateAgreement.response_id
                  )
                } finally {
                  setIsBAADownloading(false)
                }
              }}
            >
              VIEW BAA
            </Button>
          </Flex>
        ) : (
          <Flex className="mt-4">
            <Button
              $primary
              onClick={() => {
                history.push('/profile/baa')
              }}
            >
              SIGN BAA
            </Button>
          </Flex>
        )}
      </div>
    </>
  )
}

export default OrganizationTab
